import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db, auth, getCollectionName } from './firebase.js';
import {
  collection, onSnapshot, doc, updateDoc, addDoc,
  query, where, getDocs, serverTimestamp, getDoc, deleteDoc, orderBy, campos,
  onSnapshotDeVariosPais
} from './firestore-compat';
import { EmailAuthProvider, reauthenticateWithCredential } from './auth-compat';
import { isSuperUser } from './utils/roleUtils';
import ComentariosChat from './ComentariosChat';
import WizardOcorrencia from './WizardOcorrencia';
import { speak } from './utils/speechUtils';
import { supabase } from './supabase';

const DEFAULT_SLA_GC = { hospitalizacao: 24, implantacao: 48 };
const toMsGC = (ts) => ts ? (ts.toMillis ? ts.toMillis() : new Date(ts).getTime()) : 0;
const resolveOcTipo = (oc) => {
  const t = oc.paciente?.tipoOcorrencia;
  if (t === 'Implantação') return 'implantacao';
  if (t === 'Hospitalização') return 'hospitalizacao';
  // oc.tipo pode vir como 'implantacao' (lowercase) ou 'Implantação' (legado)
  if (oc.tipo === 'implantacao' || oc.tipo === 'Implantação') return 'implantacao';
  if (oc.tipo === 'hospitalizacao' || oc.tipo === 'Hospitalização') return 'hospitalizacao';
  // Ocorrências criadas pela Captação são sempre Implantação
  if (oc.captacaoId) return 'implantacao';
  return 'hospitalizacao';
};
// Último momento em que este usuário abriu o chat de uma tarefa (neste navegador)
const chatLidoKey = (tarefaId) => `oc_chat_lido_${auth.currentUser?.uid || 'anon'}_${tarefaId}`;
const getChatLido = (tarefaId) => { try { return localStorage.getItem(chatLidoKey(tarefaId)) || ''; } catch { return ''; } };
const setChatLido = (tarefaId) => { try { localStorage.setItem(chatLidoKey(tarefaId), new Date().toISOString()); } catch {} };

const ALARM_LS_KEY  = 'oc_alarm_last_play';
const ALARM_COOLDOWN = 3600000; // 1 hora

// Fala o alerta de SLA 2× seguidas com pausa de 1,5s entre elas
function speakSLAOverdue(ocorrencias, slaConfig) {
  const vencidas = ocorrencias.filter(oc => {
    if (oc.status === 'concluido') return false;
    const prazoMs = (slaConfig[resolveOcTipo(oc)] || slaConfig.hospitalizacao || 24) * 3600000;
    const abertura = toMsGC(oc.dataAbertura);
    return abertura && (Date.now() - abertura) > prazoMs;
  });

  if (vencidas.length === 0) return;

  const fmtElapsed = (oc) => {
    const ms = Date.now() - toMsGC(oc.dataAbertura);
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h} hora${h !== 1 ? 's' : ''}${m > 0 ? ` e ${m} minuto${m !== 1 ? 's' : ''}` : ''}` : `${m} minuto${m !== 1 ? 's' : ''}`;
  };

  // Fala cada ocorrência vencida — e repete 2× com pausa entre elas
  const falas = [];
  vencidas.forEach(oc => {
    const nome    = oc.paciente?.nomeCompleto || oc.paciente?.nome || 'paciente desconhecido';
    const tempo   = fmtElapsed(oc);
    const txt     = `SLA vencido. ${nome}, em atraso há ${tempo}`;
    falas.push(txt); // 1ª vez
    falas.push(txt); // 2ª vez (repetição)
  });

  // Enfileira na speech utility com pausa entre 1ª e 2ª fala
  falas.forEach((txt, i) => {
    setTimeout(() => speak(txt), i * 3500);
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────────
const PRIORIDADE_COR = { 'Crítico': '#e53e3e', 'Alta': '#e67e22', 'Média': '#ecc94b', 'Baixa': '#38a169' };
const STATUS_COR = {
  pendente:     { bg: '#f1f5f9', text: '#64748b' },
  em_andamento: { bg: '#dbeafe', text: '#1d4ed8' },
  concluido:    { bg: '#dcfce7', text: '#16a34a' },
  previa:       { bg: '#ecfeff', text: '#0891b2' },
  reconciliado: { bg: '#f0fdf4', text: '#15803d' },
  cancelado:    { bg: '#fef2f2', text: '#dc2626' },
};

function elapsed(ts) {
  if (!ts) return '--';
  let raw;
  if (ts.toMillis) raw = ts.toMillis();
  else if (ts.seconds != null) raw = ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
  else { const d = new Date(ts); raw = isNaN(d.getTime()) ? NaN : d.getTime(); }
  const ms = Date.now() - raw;
  if (ms < 0 || isNaN(ms)) return '--';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

function fmtDate(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// Tarefas de PAD têm duas etapas independentes (Coordenação e Administrativo);
// só contam como concluídas de verdade quando as duas estão concluídas.
function tarefaConcluida(t) {
  if (t.areaPai === 'PAD') return t.status === 'concluido' && t.statusAdm === 'concluido';
  return t.status === 'concluido';
}

function progress(tarefas = []) {
  if (!tarefas.length) return 0;
  return Math.round(tarefas.filter(tarefaConcluida).length / tarefas.length * 100);
}

// Classifica uma tarefa de PAD ainda não concluída: de quem é a bola (Coordenação ou Administrativo)
// e desde quando ela está parada naquela etapa.
function padEtapaPendente(t) {
  if (t.status !== 'concluido') return { lado: 'coordenacao', desde: t.dataAbertura };
  if (t.statusAdm !== 'concluido') return { lado: 'administrativo', desde: t.dataConclusao || t.dataAbertura };
  return null;
}

// Pega, entre as tarefas de PAD pendentes de uma ocorrência, a que está parada há mais tempo —
// é essa que aparece como "gargalo" na listagem principal.
function getPadBottleneck(tarefas = []) {
  const pendentes = tarefas
    .filter(t => t.areaPai === 'PAD')
    .map(t => ({ area: (t.area || '').replace('PAD - ', ''), ...padEtapaPendente(t) }))
    .filter(p => p.lado);
  if (!pendentes.length) return null;
  pendentes.sort((a, b) => toMsGC(a.desde) - toMsGC(b.desde));
  const top = pendentes[0];
  return { ...top, outras: pendentes.length - 1 };
}

function getHighestPriority(tarefas = []) {
  const levels = { 'Crítico': 4, 'Alta': 3, 'Média': 2, 'Baixa': 1 };
  let max = 0;
  let maxPrio = '';
  for (const t of tarefas) {
    if (t.status === 'concluido') continue;
    const l = levels[t.prioridade] || 0;
    if (l > max) { max = l; maxPrio = t.prioridade; }
  }
  return maxPrio;
}

// ─── Badge components ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS_COR[status] || STATUS_COR.pendente;
  const label = { pendente: 'Pendente', em_andamento: 'Em Andamento', concluido: 'Concluído', previa: 'Prévia', reconciliado: 'Reconciliada', cancelado: 'Cancelado' }[status] || status;
  return (
    <span style={{ background: s.bg, color: s.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function PriorBadge({ p }) {
  const c = PRIORIDADE_COR[p] || '#94a3b8';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: c, fontSize: 11, fontWeight: 700 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />
      {p || '--'}
    </span>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────────
export default function GestaoOcorrencias({ setorFilter: setorFilterProp, sectorNome }) {
  const [ocorrencias, setOcorrencias] = useState({});
  const [userData, setUserData] = useState(null);
  const [users, setUsers] = useState([]);
  const [grupoSetores, setGrupoSetores] = useState(null); // null = loading, [] = sem grupo
  const knownOcIdsRef = useRef(null); // null = primeira carga (não fala); Set = IDs já conhecidos
  const [activeTab, setActiveTab] = useState('aberto');
  const [expanded, setExpanded] = useState({});
  const [padExpanded, setPadExpanded] = useState({});
  const [prontExpanded, setProntExpanded] = useState({});
  const [prontAdding, setProntAdding] = useState({}); // ocId → true = mostrando selector
  const [prontSelecionados, setProntSelecionados] = useState({}); // ocId → Set de labels
  const [chatOpen, setChatOpen] = useState(false);
  const [chatConfig, setChatConfig] = useState({});
  const [filtroArea, setFiltroArea] = useState('');
  const [filtroPrio, setFiltroPrio] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [editMode, setEditMode] = useState({});
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState({});
  const [passwordModal, setPasswordModal] = useState(null);
  const [slaConfig, setSlaConfig] = useState(DEFAULT_SLA_GC);
  // alarmCtxRef removido — substituído por voz sintética
  const [showWizard, setShowWizard] = useState(false);
  const [cooperativas, setCooperativas] = useState([]);
  const [empresasTerceiras, setEmpresasTerceiras] = useState([]);
  const [canceladosMap, setCanceladosMap] = useState({}); // captacaoId → { canceladoEm }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, getCollectionName('cooperativas_config')), orderBy('name')),
      snap => setCooperativas(snap.docs.map(d => d.data().name).filter(Boolean)),
      () => {}
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, getCollectionName('empresas_terceiras_config')), orderBy('name')),
      snap => {
        const list = snap.docs.map(d => d.data().name).filter(Boolean);
        if (list.length > 0) {
          setEmpresasTerceiras(list);
        } else {
          setEmpresasTerceiras(["Dismed", "Humana", "Quality", "Homed", "Outra"]);
        }
      },
      () => {
        setEmpresasTerceiras(["Dismed", "Humana", "Quality", "Homed", "Outra"]);
      }
    );
    return () => unsub();
  }, []);

  // Self-heal: prévias com ocorrência aberto/em_andamento no mesmo captacaoId são ocultadas da aba Prévias
  useEffect(() => {
    const fix = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('status', 'in', ['previa', 'reconciliado'])
        ));
        const comCaptacao = snap.docs.filter(d => d.data().captacaoId && !d.data().previaSubstituida);
        for (const previaDoc of comCaptacao) {
          const captacaoId = previaDoc.data().captacaoId;
          const abertos = await getDocs(query(
            collection(db, getCollectionName('ocorrencias_v2')),
            where('captacaoId', '==', captacaoId),
            where('status', 'in', ['aberto', 'em_andamento'])
          ));
          if (!abertos.empty) {
            await updateDoc(previaDoc.ref, {
              status: 'reconciliado',
              previaSubstituida: true,
              dataAtualizacao: serverTimestamp(),
            });
          }
        }
      } catch (e) { /* silent */ }
    };
    fix();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load cancelled captação patients for black-row and Cancelados tab
  useEffect(() => {
    const q = query(
      collection(db, getCollectionName('captacao_pacientes')),
      where('canceladoPelaOperadora', '==', true),
      campos('canceladoEm')
    );
    return onSnapshot(q, snap => {
      const map = {};
      snap.docs.forEach(d => {
        map[d.id] = { canceladoEm: d.data().canceladoEm || null };
      });
      setCanceladosMap(map);
    }, () => {});
  }, []);

  const isSuperAdmin = isSuperUser(auth.currentUser?.email);
  const isAdmin = userData?.roles?.includes('Admin') || isSuperAdmin;
  // Grupos config tem prioridade — contém as áreas reais de ocorrência vinculadas ao setor.
  // A prop (nome do setor) só serve de fallback para usuários sem grupo cadastrado.
  const setorFilter = (grupoSetores?.length ? grupoSetores : null) || setorFilterProp || null;

  const isOcCancelado = (oc) => !!(oc.captacaoId && canceladosMap[oc.captacaoId]);
  const isCanceladoHa6h = (oc) => {
    if (!oc.captacaoId || !canceladosMap[oc.captacaoId]) return false;
    const em = canceladosMap[oc.captacaoId]?.canceladoEm;
    if (!em) return false;
    return (Date.now() - new Date(em).getTime()) >= 6 * 3600 * 1000;
  };

  const handleDeleteOcorrencia = useCallback(async (oc) => {
    if (!window.confirm(`Excluir permanentemente a ocorrência de "${oc.paciente?.nomeCompleto || oc.nomePaciente}"?\n\nEssa ação não pode ser desfeita.`)) return;
    try {
      // Exclui todas as tarefas da ocorrência primeiro
      const tarefasSnap = await getDocs(collection(db, getCollectionName('ocorrencias_v2'), oc.id, 'tarefas'));
      for (const t of tarefasSnap.docs) {
        await deleteDoc(doc(db, getCollectionName('ocorrencias_v2'), oc.id, 'tarefas', t.id));
      }
      await deleteDoc(doc(db, getCollectionName('ocorrencias_v2'), oc.id));
    } catch (e) {
      alert('Erro ao excluir: ' + e.message);
    }
  }, []);

  const handleDeleteTarefa = useCallback(async (ocId, tarefaId, area) => {
    if (!window.confirm(`Excluir a etapa "${area}" permanentemente?`)) return;
    try {
      await deleteDoc(doc(db, getCollectionName('ocorrencias_v2'), ocId, 'tarefas', tarefaId));
    } catch (e) {
      alert('Erro ao excluir etapa: ' + e.message);
    }
  }, []);

  // Load SLA config
  useEffect(() => {
    getDoc(doc(db, getCollectionName('configuracoes_v2'), 'sla'))
      .then(d => { if (d.exists()) setSlaConfig({ ...DEFAULT_SLA_GC, ...d.data() }); })
      .catch(() => {});
  }, []);

  // Load current user data
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    const q = query(collection(db, getCollectionName('users')), where('email', '==', u.email?.toLowerCase()));
    return onSnapshot(q, snap => {
      if (!snap.empty) setUserData({ id: snap.docs[0].id, ...snap.docs[0].data() });
    });
  }, []);

  // Carrega grupos de implantação e descobre os setores do usuário logado.
  // Super-usuários acessando diretamente (sem setorFilterProp) veem tudo — sem filtro de grupo.
  // Mas se chegaram via OcorrenciaSetores (com setorFilterProp), o lookup é feito para traduzir
  // o nome do setor para as áreas de ocorrência configuradas no grupo.
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) { setGrupoSetores([]); return; }
    // Sem setorFilterProp = acesso ao painel completo; grupo não deve filtrar
    if (!setorFilterProp) { setGrupoSetores([]); return; }
    getDoc(doc(db, getCollectionName('captacao_config'), 'grupos_implantacao')).then(snap => {
      if (!snap.exists()) { setGrupoSetores([]); return; }
      const grupos = snap.data().grupos || [];
      const email = u.email?.toLowerCase();
      // Coleta todos os grupos do usuário (não só o primeiro) para suportar múltiplos setores
      const meusGrupos = grupos.filter(g => (g.membros || []).some(m => m.toLowerCase() === email));
      const todosSetores = [...new Set(meusGrupos.flatMap(g => g.setores || []))];
      setGrupoSetores(todosSetores);
    }).catch(() => setGrupoSetores([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load all users for responsável dropdown
  useEffect(() => {
    getDocs(collection(db, getCollectionName('users')))
      .then(snap => {
        const lista = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.name && u.name.trim()) // campo 'name' no banco
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(lista);
      })
      .catch(err => console.error('Erro ao carregar usuários:', err));
  }, []);

  // Em visão de setor (setorFilter), carregamos tudo e filtramos pelo status da TAREFA do setor,
  // não pelo status global da ocorrência. Isso permite que o time de Prontuário, por exemplo,
  // veja sua tarefa como concluída mesmo que PAD ou Farmácia ainda estejam pendentes.
  const isSectorView = !!setorFilterProp || (grupoSetores !== null && grupoSetores.length > 0);

  // Load ocorrências
  useEffect(() => {
    knownOcIdsRef.current = null; // reseta ao trocar de aba/visão para não disparar voz na recarga
    const ref = collection(db, getCollectionName('ocorrencias_v2'));
    let q;
    if (isSectorView) {
      q = query(ref);
    } else if (activeTab === 'previa') {
      q = query(ref, where('status', 'in', ['previa', 'reconciliado', 'cancelado']));
    } else if (activeTab === 'aberto' || activeTab === 'cancelados') {
      q = query(ref, where('status', 'in', ['aberto', 'em_andamento']));
    } else {
      q = query(ref, where('status', '==', 'concluido'));
    }
    return onSnapshot(q, snap => {
      const isFirstLoad = knownOcIdsRef.current === null;
      const currentKnown = knownOcIdsRef.current || new Set();
      const nextDocs = {};

      snap.docs.forEach(d => {
        const data = d.data();
        const pTipo = data.paciente?.tipoOcorrencia;
        if (pTipo === 'Implantação' || data.tipo === 'Implantação') data.tipo = 'implantacao';
        else if (pTipo === 'Hospitalização' || data.tipo === 'Hospitalização') data.tipo = 'hospitalizacao';
        nextDocs[d.id] = { id: d.id, ...data };
      });

      // Detecta novas ocorrências de implantação e fala o alerta (só em "Minhas Ocorrências")
      if (!isFirstLoad && isSectorView) {
        Object.values(nextDocs).forEach(oc => {
          if (!currentKnown.has(oc.id) && oc.tipo === 'implantacao' && oc.status !== 'concluido') {
            const pacNome = oc.paciente?.nomeCompleto || oc.paciente?.nome || 'paciente';
            speak(`Entrou uma nova ocorrência de implantação para o paciente ${pacNome}`);
          }
        });
      }

      knownOcIdsRef.current = new Set(Object.keys(nextDocs));

      setOcorrencias(prev => {
        const next = {};
        Object.entries(nextDocs).forEach(([id, data]) => {
          next[id] = { ...data, tarefas: prev[id]?.tarefas || [] };
        });
        return next;
      });
    });
  }, [activeTab, isSectorView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load tarefas de todas as ocorrências com uma assinatura só (antes: um onSnapshot por
  // ocorrência — 551 na visão de setor, ~147 mil consultas em 8h; ver onSnapshotDeVariosPais)
  useEffect(() => {
    const ids = Object.keys(ocorrencias);
    if (!ids.length) return;
    return onSnapshotDeVariosPais(getCollectionName('ocorrencias_v2'), 'tarefas', ids, porPai => {
      setOcorrencias(prev => {
        const next = { ...prev };
        Object.entries(porPai).forEach(([id, lista]) => {
          if (!next[id]) return;
          const tarefas = lista.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
          next[id] = { ...next[id], tarefas };
        });
        return next;
      });
    });
  }, [Object.keys(ocorrencias).join(',')]);

  // Resumo dos chats (💬) de cada tarefa: total de mensagens e a última, para o contador/notificação
  const [chatResumo, setChatResumo] = useState({});
  const tarefaIdsKey = Object.values(ocorrencias).flatMap(oc => (oc.tarefas || []).map(t => t.id)).sort().join(',');
  useEffect(() => {
    if (chatOpen) return;
    const ids = tarefaIdsKey ? tarefaIdsKey.split(',') : [];
    if (!ids.length) { setChatResumo({}); return; }
    let cancelado = false;
    // A tabela de comentários é pequena (~1 mil linhas em 10/2026): buscar só os 3 campos de
    // todas, em páginas de 1000, sai em 1-2 consultas. Antes eram lotes de 100 tarefas — ~30
    // consultas a cada 2 min por pessoa com a tela aberta.
    const idsSet = new Set(ids);
    const carregar = async () => {
      if (document.visibilityState === 'hidden') return;
      const resumo = {};
      for (let start = 0; ; start += 1000) {
        const { data, error } = await supabase.from('ocorrencias_comentarios')
          .select('parent_id, ts:data->>timestamp, autor:data->>autorUid')
          .order('id').range(start, start + 999);
        if (error) { console.warn('[GestaoOcorrencias] resumo dos chats:', error.message); return; }
        (data || []).forEach(r => {
          if (!idsSet.has(r.parent_id)) return;
          const cur = resumo[r.parent_id] || { total: 0, ultimo: '', ultimoAutor: '' };
          cur.total++;
          if ((r.ts || '') > cur.ultimo) { cur.ultimo = r.ts || ''; cur.ultimoAutor = r.autor || ''; }
          resumo[r.parent_id] = cur;
        });
        if (!data || data.length < 1000) break;
      }
      if (!cancelado) setChatResumo(resumo);
    };
    carregar();
    const timer = setInterval(carregar, 120000);
    return () => { cancelado = true; clearInterval(timer); };
  }, [tarefaIdsKey, chatOpen]);

  const handleStartEdit = useCallback((oc) => {
    const pac = oc.paciente || {};
    setEditForm(prev => ({
      ...prev,
      [oc.id]: {
        nomeCompleto: pac.nomeCompleto || '',
        dataNascimento: pac.dataNascimento || '',
        convenio: pac.convenio || '',
        numAtendimento: pac.numAtendimento || '',
        numIW: pac.numIW || oc.numeroIW || '',
        classificacao: pac.classificacao || '',
        categoria: pac.categoria || '',
        possuiLiminar: pac.possuiLiminar || false,
        tipo: pac.tipo || '',
        tipoOcorrencia: pac.tipoOcorrencia || '',
        observacoes: pac.observacoes || '',
      }
    }));
    setEditMode(prev => ({ ...prev, [oc.id]: true }));
  }, []);

  const handleCancelEdit = useCallback((ocId) => {
    setEditMode(prev => ({ ...prev, [ocId]: false }));
    setEditForm(prev => { const n = { ...prev }; delete n[ocId]; return n; });
  }, []);

  const handleSaveRequest = useCallback((ocId) => {
    setPasswordModal({ ocId });
  }, []);

  const handleConfirmSave = useCallback(async (password) => {
    if (!passwordModal) return;
    const { ocId } = passwordModal;
    setSavingEdit(prev => ({ ...prev, [ocId]: true }));
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      const form = editForm[ocId];
      const updatedPaciente = { ...(ocorrencias[ocId]?.paciente || {}), ...form };
      updatedPaciente.numAtendimento = form.numIW || '';
      await updateDoc(doc(db, getCollectionName('ocorrencias_v2'), ocId), {
        paciente: updatedPaciente,
        numeroIW: form.numIW || '',
        tipo: form.tipoOcorrencia === 'Hospitalização' ? 'hospitalizacao' : 'implantacao',
        dataAtualizacao: serverTimestamp(),
      });
      setPasswordModal(null);
      setEditMode(prev => ({ ...prev, [ocId]: false }));
      setEditForm(prev => { const n = { ...prev }; delete n[ocId]; return n; });
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        alert('Senha incorreta. Tente novamente.');
      } else {
        alert('Erro ao salvar: ' + err.message);
      }
    } finally {
      setSavingEdit(prev => ({ ...prev, [ocId]: false }));
    }
  }, [passwordModal, editForm, ocorrencias]);

  const handleUpdateTarefa = useCallback(async (ocId, tarefaId, campo, valor) => {
    const ref = doc(db, getCollectionName('ocorrencias_v2'), ocId, 'tarefas', tarefaId);
    let upd;
    if (typeof campo === 'object' && campo !== null) {
      upd = { ...campo };
    } else {
      upd = { [campo]: valor };
    }
    if (upd.status === 'concluido') upd.dataConclusao = serverTimestamp();
    if (upd.status && upd.status !== 'concluido') upd.dataConclusao = null;
    // Etapa do Administrativo (só PAD) — espelha a mesma lógica de data de conclusão, independente da Coordenação
    if (upd.statusAdm === 'concluido') upd.dataConclusaoAdm = serverTimestamp();
    if (upd.statusAdm && upd.statusAdm !== 'concluido') upd.dataConclusaoAdm = null;
    await updateDoc(ref, upd);

    // Check if all done → complete occurrence
    const snap = await getDocs(collection(db, getCollectionName('ocorrencias_v2'), ocId, 'tarefas'));
    const all = snap.docs.map(d => d.data());

    // Mantém lista desnormalizada de responsáveis para query de notificação
    if (upd.responsavelUid !== undefined) {
      const uids = [...new Set(all.map(t => t.responsavelUid).filter(Boolean))];
      const ocRef = doc(db, getCollectionName('ocorrencias_v2'), ocId);
      const oc = ocorrencias[ocId];
      if (oc?.criadoPorUid && !uids.includes(oc.criadoPorUid)) uids.push(oc.criadoPorUid);
      await updateDoc(ocRef, { responsaveisUids: uids });
    }

    // Tarefas de PAD só contam como concluídas quando Coordenação E Administrativo concluírem
    if (all.length && all.every(tarefaConcluida)) {
      await updateDoc(doc(db, getCollectionName('ocorrencias_v2'), ocId), { status: 'concluido', dataConclusao: serverTimestamp() });
    } else if (all.some(t => t.status !== 'pendente' || (t.areaPai === 'PAD' && t.statusAdm && t.statusAdm !== 'pendente'))) {
      const oc = ocorrencias[ocId];
      if (oc?.status === 'aberto') {
        await updateDoc(doc(db, getCollectionName('ocorrencias_v2'), ocId), { status: 'em_andamento' });
      }
    }
  }, [ocorrencias]);

  const PRONTUARIO_ITENS = [
    'Pasta Fixa', 'Guia / Autorização', 'Prescrição Médica',
    'TCLE', 'Cronograma de Visitas', 'Admissão / Alta Hospitalar',
  ];


  const handleAddProntSubtasks = useCallback(async (ocId, rootTaskId, itens) => {
    if (!itens || itens.length === 0) return;
    const tarefasCol = collection(db, getCollectionName('ocorrencias_v2'), ocId, 'tarefas');
    const snap = await getDocs(tarefasCol);
    let maxOrdem = Math.max(0, ...snap.docs.map(d => d.data().ordem || 0));
    for (const label of itens) {
      await addDoc(tarefasCol, {
        area: `Prontuário - ${label}`,
        areaPai: 'Prontuário',
        status: 'pendente',
        prioridade: 'Média',
        dataAbertura: serverTimestamp(),
        dataConclusao: null,
        ordem: ++maxOrdem,
        descricao: '',
        responsavelNome: '',
        responsavelUid: '',
        criadoPorNome: userData?.name || auth.currentUser?.displayName || '',
        criadoPorUid: auth.currentUser?.uid || '',
      });
    }
    // remove task root se existir (foi substituída pelas sub-tarefas)
    if (rootTaskId) {
      await deleteDoc(doc(db, getCollectionName('ocorrencias_v2'), ocId, 'tarefas', rootTaskId));
    }
    setProntAdding(p => ({ ...p, [ocId]: false }));
    setProntSelecionados(p => ({ ...p, [ocId]: new Set() }));
  }, [userData]);

  const sortedList = useMemo(() => {
    return Object.values(ocorrencias).sort((a, b) => {
      const ta = toMsGC(a.dataAbertura);
      const tb = toMsGC(b.dataAbertura);
      return ta - tb; // oldest first
    });
  }, [ocorrencias]);

  const filteredList = useMemo(() => {
    // Cancelados tab: ocorrências cujo paciente foi cancelado há +6h
    if (activeTab === 'cancelados') {
      const base = sortedList.filter(oc => isCanceladoHa6h(oc));
      if (!setorFilter) return base;
      const setores = Array.isArray(setorFilter) ? setorFilter : [setorFilter];
      return base.filter(oc =>
        (oc.tarefas || []).some(t => setores.some(s => t.area === s || t.areaPai === s))
      );
    }

    // Prévias tab: mostra previa/reconciliado/cancelado, exceto prévias já substituídas por aberto
    // e exceto cancelados há +6h (esses migram para a aba "Cancelados")
    if (activeTab === 'previa') {
      const previaStatuses = new Set(['previa', 'reconciliado', 'cancelado']);
      const base = sortedList.filter(oc =>
        previaStatuses.has(oc.status) && !oc.previaSubstituida && !isCanceladoHa6h(oc)
      );
      if (!setorFilter) return base;
      const setores = Array.isArray(setorFilter) ? setorFilter : [setorFilter];
      return base.filter(oc =>
        (oc.tarefas || []).some(t => setores.some(s => t.area === s || t.areaPai === s))
      );
    }

    // Remove prévias E ocorrências canceladas há +6h das abas normais
    const semPrevia = sortedList.filter(oc => !['previa','reconciliado'].includes(oc.status) && !isCanceladoHa6h(oc));

    if (!setorFilter) return semPrevia;
    const setores = Array.isArray(setorFilter) ? setorFilter : [setorFilter];
    // 1. só ocorrências que têm tarefa deste setor
    let list = semPrevia.filter(oc =>
      (oc.tarefas || []).some(t => setores.some(s => t.area === s || t.areaPai === s))
    );
    // 2. em visão de setor, a aba filtra por status da TAREFA do setor, não da ocorrência
    if (activeTab === 'aberto') {
      list = list.filter(oc =>
        (oc.tarefas || []).some(t =>
          setores.some(s => t.area === s || t.areaPai === s) && t.status !== 'concluido'
        )
      );
    } else {
      // Concluídas: todas as tarefas do setor estão concluídas
      list = list.filter(oc =>
        (oc.tarefas || []).every(t =>
          !setores.some(s => t.area === s || t.areaPai === s) || t.status === 'concluido'
        )
      );
    }
    return list;
  }, [sortedList, setorFilter, activeTab, canceladosMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const overdueIds = useMemo(() => {
    const result = new Set();
    filteredList.forEach(oc => {
      if (oc.status === 'concluido') return;
      const prazoMs = (slaConfig[resolveOcTipo(oc)] || slaConfig.hospitalizacao || 24) * 3600000;
      const abertura = toMsGC(oc.dataAbertura);
      if (abertura && (Date.now() - abertura) > prazoMs) result.add(oc.id);
    });
    return result;
  }, [filteredList, slaConfig]);

  // Alerta de voz: fala "SLA vencido [paciente] em atraso há X" 2× seguidas
  // Repete somente após 1h de cooldown (localStorage)
  useEffect(() => {
    if (overdueIds.size === 0) return;
    const trySpeak = () => {
      const last = Number(localStorage.getItem(ALARM_LS_KEY) || 0);
      if (Date.now() - last >= ALARM_COOLDOWN) {
        localStorage.setItem(ALARM_LS_KEY, String(Date.now()));
        speakSLAOverdue(filteredList, slaConfig);
      }
    };
    trySpeak();
    const interval = setInterval(trySpeak, 60000);
    return () => clearInterval(interval);
  }, [overdueIds.size]); // eslint-disable-line

  // Resumo do fluxo PAD (Coordenação → Administrativo) entre as ocorrências em aberto
  const resumoPad = useMemo(() => {
    const setores = setorFilter ? (Array.isArray(setorFilter) ? setorFilter : [setorFilter]) : null;
    let total = 0, aguardandoCoordenacao = 0, emAndamentoAdm = 0, pendenteAdm = 0;
    Object.values(ocorrencias).forEach(oc => {
      if (oc.status === 'concluido' || ['previa', 'reconciliado', 'cancelado'].includes(oc.status)) return;
      let padTasks = (oc.tarefas || []).filter(t => t.areaPai === 'PAD');
      if (setores) padTasks = padTasks.filter(t => setores.some(s => t.area === s || t.areaPai === s));
      if (!padTasks.length) return;
      total++;
      if (padTasks.some(t => t.status !== 'concluido')) {
        aguardandoCoordenacao++;
      } else if (padTasks.some(t => t.statusAdm === 'em_andamento')) {
        emAndamentoAdm++;
      } else if (padTasks.some(t => t.statusAdm !== 'concluido')) {
        pendenteAdm++;
      }
    });
    return { total, aguardandoCoordenacao, emAndamentoAdm, pendenteAdm };
  }, [ocorrencias, setorFilter]);

  // Mesma base de exclusão usada pela aba "Em Aberto" (prévia/reconciliado/cancelado e
  // cancelados há +6h) — os cards de totalização precisam contar exatamente o que a
  // lista mostra, nunca mais do que isso.
  const ocorrenciasAbertasBase = useMemo(() => {
    return Object.values(ocorrencias).filter(oc =>
      !['previa', 'reconciliado', 'cancelado'].includes(oc.status) &&
      oc.status !== 'concluido' &&
      !isCanceladoHa6h(oc)
    );
  }, [ocorrencias, canceladosMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Total de ocorrências em aberto por setor do usuário (visão "Minhas Ocorrências") —
  // cada setor mostra só o total dele, em vez de um único número misturando tudo.
  const resumoPorArea = useMemo(() => {
    if (!setorFilter) return [];
    const setores = Array.isArray(setorFilter) ? setorFilter : [setorFilter];
    return setores.map(area => {
      const ocsArea = ocorrenciasAbertasBase.filter(oc =>
        (oc.tarefas || []).some(t => (t.areaPai === area || t.area === area) && t.status !== 'concluido')
      );
      if (area !== 'PAD') return { area, total: ocsArea.length };

      let aguardandoCoordenacao = 0, emAndamentoAdm = 0, pendenteAdm = 0;
      ocsArea.forEach(oc => {
        const padTasks = (oc.tarefas || []).filter(t => t.areaPai === 'PAD');
        if (padTasks.some(t => t.status !== 'concluido')) aguardandoCoordenacao++;
        else if (padTasks.some(t => t.statusAdm === 'em_andamento')) emAndamentoAdm++;
        else if (padTasks.some(t => t.statusAdm !== 'concluido')) pendenteAdm++;
      });
      return { area, total: ocsArea.length, aguardandoCoordenacao, emAndamentoAdm, pendenteAdm };
    });
  }, [ocorrenciasAbertasBase, setorFilter]);

  const AREA_CARD_STYLE = {
    'PAD':               { bg: '#f1f5f9', cor: '#334155', icone: '📋' },
    'Mobiliário':        { bg: '#f5f3ff', cor: '#6d28d9', icone: '🛋️' },
    'Gases Medicinais':  { bg: '#eff6ff', cor: '#1d4ed8', icone: '🫧' },
    'Equipamentos':      { bg: '#fffbeb', cor: '#a16207', icone: '🛠️' },
    'Prontuário':        { bg: '#f0fdfa', cor: '#0f766e', icone: '📁' },
    'Farmácia':          { bg: '#fff7ed', cor: '#c2410c', icone: '💊' },
    'Logística':         { bg: '#f8fafc', cor: '#475569', icone: '🚚' },
    'Suprimentos':       { bg: '#fdf4ff', cor: '#a21caf', icone: '📦' },
  };

  // ─── STYLES ──────────────────────────────────────────────────────────────────
  const TH = { padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', textAlign: 'left' };
  const TD = { padding: '4px 10px', fontSize: 12, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160, textAlign: 'left' };
  const SELECT_INLINE = { border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 6px', fontSize: 11, background: '#fff', cursor: 'pointer', width: '100%' };

  const openChat = (ocId, tarefaId, titulo) => { setChatLido(tarefaId); setChatConfig({ ocorrenciaId: ocId, tarefaId, tarefaTitulo: titulo }); setChatOpen(true); };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: '#f1f5f9', minHeight: '100vh', padding: 0 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .oc-row:hover { background: #f8fafc !important; }
        .tarefa-row:hover { background: #f0f9ff !important; }
        .btn-oc:hover { opacity: 0.85; }
        .oc-cancelado td { color: #e2e8f0 !important; }
        .oc-cancelado:hover { background: #1f2937 !important; }
      `}</style>

      {/* ─── TOP BAR ─────────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🏥</span>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: '-0.3px' }}>
            {setorFilter
              ? (Array.isArray(setorFilter) ? setorFilter.join(' + ') : String(setorFilter))
              : (sectorNome || 'Gestão de Ocorrências')}
          </span>
          <span style={{ background: 'rgba(255,255,255,0.15)', color: '#e2e8f0', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
            {filteredList.length} {activeTab === 'aberto' ? 'abertas' : activeTab === 'previa' ? 'prévias' : activeTab === 'cancelados' ? 'canceladas' : 'concluídas'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={filtroArea} onChange={e => setFiltroArea(e.target.value)} style={{ padding: '5px 10px', borderRadius: 8, border: 'none', fontSize: 12, background: 'rgba(255,255,255,0.12)', color: '#e2e8f0', cursor: 'pointer' }}>
            <option value="">Todas as áreas</option>
            <option>PAD</option><option>Exames</option><option>Remoções</option>
            <option>Mobiliário</option><option>Equipamentos</option><option>Farmácia</option>
            <option>Prontuário</option><option>Gases Medicinais</option><option>Logística</option><option>Suprimentos</option>
          </select>
          <select value={filtroPrio} onChange={e => setFiltroPrio(e.target.value)} style={{ padding: '5px 10px', borderRadius: 8, border: 'none', fontSize: 12, background: 'rgba(255,255,255,0.12)', color: '#e2e8f0', cursor: 'pointer' }}>
            <option value="">Todas prioridades</option>
            <option>Crítico</option><option>Alta</option><option>Média</option><option>Baixa</option>
          </select>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ padding: '5px 10px', borderRadius: 8, border: 'none', fontSize: 12, background: 'rgba(255,255,255,0.12)', color: '#e2e8f0', cursor: 'pointer' }}>
            <option value="">Todos status</option>
            <option value="pendente">Pendente</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="concluido">Concluído</option>
          </select>
          {(filtroArea || filtroPrio || filtroStatus) && (
            <button onClick={() => { setFiltroArea(''); setFiltroPrio(''); setFiltroStatus(''); }}
              style={{ padding: '5px 12px', borderRadius: 8, border: 'none', fontSize: 11, background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
              ✕ Limpar
            </button>
          )}
          <button onClick={() => setShowWizard(true)} style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', padding: '7px 18px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, boxShadow: '0 4px 12px rgba(102,126,234,0.4)', cursor: 'pointer' }}>
            + Nova Ocorrência
          </button>
        </div>
      </div>

      {/* ─── TABS + TOTALIZADORES ─────────────────────────────────────────────── */}
      <>

      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 0 }}>
        {[['aberto', 'Em Aberto'], ['previa', '🗒 Prévias'], ['concluido', 'Concluídas'], ['cancelados', '⚫ Cancelados']].map(([val, label]) => (
          <button key={val} onClick={() => setActiveTab(val)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === val ? 700 : 500, color: activeTab === val ? (val === 'previa' ? '#0891b2' : val === 'cancelados' ? '#111827' : '#4f46e5') : '#64748b', borderBottom: activeTab === val ? `2px solid ${val === 'previa' ? '#0891b2' : val === 'cancelados' ? '#111827' : '#4f46e5'}` : '2px solid transparent', marginBottom: -1, transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}

      </div>

      {/* ─── RESUMO: visão "Minhas Ocorrências" (setorFilter) → um total por setor ── */}
      {activeTab === 'aberto' && setorFilter && resumoPorArea.some(r => r.total > 0) && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '16px 24px 0' }}>
          {resumoPorArea.map(({ area, total, aguardandoCoordenacao, emAndamentoAdm, pendenteAdm }) => {
            const st = AREA_CARD_STYLE[area] || { bg: '#f1f5f9', cor: '#334155', icone: '📌' };
            return (
              <div key={area} style={{ flex: '1 1 200px', minWidth: 200, background: st.bg, borderRadius: 12, padding: '14px 18px', border: `1px solid ${st.cor}22` }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: st.cor, lineHeight: 1.1 }}>{st.icone} {total}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: st.cor, marginTop: 4 }}>{area} em aberto</div>
                {area === 'PAD' && total > 0 && (
                  <div style={{ fontSize: 10.5, color: st.cor, opacity: 0.75, marginTop: 6, lineHeight: 1.5 }}>
                    ⏳ {aguardandoCoordenacao} aguard. coordenação · ⚙️ {emAndamentoAdm} em andamento adm. · 📥 {pendenteAdm} pendente adm.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── RESUMO PAD (visão geral/admin, sem setorFilter): Coordenação × Administrativo ── */}
      {activeTab === 'aberto' && !setorFilter && resumoPad.total > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '16px 24px 0' }}>
          {[
            { label: 'Ocorrências com PAD em aberto', valor: resumoPad.total, bg: '#f1f5f9', cor: '#334155', icone: '📋' },
            { label: 'Aguardando Coordenação de Área', valor: resumoPad.aguardandoCoordenacao, bg: '#fffbeb', cor: '#a16207', icone: '⏳' },
            { label: 'Em Andamento pelo Administrativo', valor: resumoPad.emAndamentoAdm, bg: '#eff6ff', cor: '#1d4ed8', icone: '⚙️' },
            { label: 'Pendente com o Administrativo', valor: resumoPad.pendenteAdm, bg: '#fef2f2', cor: '#b91c1c', icone: '📥' },
          ].map(card => (
            <div key={card.label} style={{ flex: '1 1 200px', minWidth: 200, background: card.bg, borderRadius: 12, padding: '14px 18px', border: `1px solid ${card.cor}22` }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: card.cor, lineHeight: 1.1 }}>{card.icone} {card.valor}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: card.cor, marginTop: 4 }}>{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ─── TABLE ───────────────────────────────────────────────────────────── */}
      <div style={{ padding: '0', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 1100 }}>
          <colgroup>
            <col style={{ width: 32 }} />   {/* expand arrow */}
            <col style={{ width: 6 }} />    {/* priority stripe */}
            <col style={{ width: 170 }} />  {/* patient name */}
            <col style={{ width: 85 }} />   {/* nº IW */}
            <col style={{ width: 75 }} />   {/* tipo */}
            <col style={{ width: 65 }} />   {/* classif */}
            <col style={{ width: 65 }} />   {/* categoria */}
            <col style={{ width: 55 }} />   {/* liminar */}
            <col style={{ width: 100 }} />  {/* convênio */}
            <col style={{ width: 80 }} />   {/* abertura */}
            <col style={{ width: 65 }} />   {/* tempo */}
            <col style={{ width: 100 }} />  {/* progresso */}
            <col style={{ width: 90 }} />   {/* status */}
            <col style={{ width: 80 }} />   {/* prioridade */}
          </colgroup>
          <thead>
            <tr>
              <th style={TH}></th>
              <th style={TH}></th>
              <th style={TH}>Paciente</th>
              <th style={TH}>Nº IW</th>
              <th style={TH}>Tipo</th>
              <th style={TH}>Classif.</th>
              <th style={TH}>Categ.</th>
              <th style={TH}>Liminar</th>
              <th style={TH}>Convênio</th>
              <th style={TH}>Abertura</th>
              <th style={TH}>Tempo</th>
              <th style={TH}>Progresso</th>
              <th style={TH}>Status</th>
              <th style={TH}>Prioridade</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 && (
              <tr>
                <td colSpan={14} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>
                  Nenhuma ocorrência {activeTab === 'aberto' ? 'em aberto' : activeTab === 'previa' ? 'prévia' : activeTab === 'cancelados' ? 'cancelada' : 'concluída'}{setorFilter ? ` para o setor "${setorFilter}"` : ''}.
                </td>
              </tr>
            )}
            {filteredList.map(oc => {
              const isExp = expanded[oc.id];
              const pct = progress(oc.tarefas);
              const autoPrio = getHighestPriority(oc.tarefas);
              const isOverdue = overdueIds.has(oc.id);
              const ocCancelado = isOcCancelado(oc);
              const borda = ocCancelado ? '#111827' : (isOverdue ? '#dc2626' : (PRIORIDADE_COR[autoPrio] || '#e2e8f0'));
              const pac = oc.paciente || {};

              // Tarefas visíveis após filtros
              const setoresFiltro = setorFilter ? (Array.isArray(setorFilter) ? setorFilter : [setorFilter]) : null;
              let tarefasVisiveis = (oc.tarefas || []).filter(t => {
                if (setoresFiltro && !setoresFiltro.some(s => t.area === s || t.areaPai === s)) return false;
                if (filtroArea && t.area !== filtroArea && t.areaPai !== filtroArea) return false;
                if (filtroPrio && t.prioridade !== filtroPrio) return false;
                if (filtroStatus && t.status !== filtroStatus) return false;
                return true;
              });
              // 2026-10-06: o "gargalo" usava oc.tarefas (todas as disciplinas, sem filtro de
              // setor) — um coordenador de Fono via o status de pendência de Fisio aparecer
              // aqui, mesmo com as linhas da tabela já filtradas certo. Usa tarefasVisiveis
              // pra bater com o que a visão do setor realmente deveria mostrar.
              const bottleneck = oc.status !== 'concluido' ? getPadBottleneck(tarefasVisiveis) : null;

              const padTasks = tarefasVisiveis.filter(t => t.areaPai === 'PAD');
              // rootTasks inclui a raiz de Prontuário
              const rootTasks = tarefasVisiveis.filter(t => !t.areaPai);

              return (
                <React.Fragment key={oc.id}>
                  {/* ─── PATIENT ROW ────────────────────────────────────────── */}
                  <tr
                    className={`oc-row${ocCancelado ? ' oc-cancelado' : ''}`}
                    onClick={() => setExpanded(p => ({ ...p, [oc.id]: !p[oc.id] }))}
                    style={{ cursor: 'pointer', background: ocCancelado ? '#111827' : (isOverdue ? '#fff5f5' : '#fff'), borderBottom: '1px solid #e2e8f0', transition: 'background 0.1s' }}
                  >
                    <td style={{ ...TD, textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>{isExp ? '▼' : '▶'}</td>
                    <td style={{ padding: 0 }}>
                      <div style={{ width: 4, height: 36, background: borda, borderRadius: '0 3px 3px 0' }} />
                    </td>
                    <td style={{ ...TD, fontWeight: 700, color: '#1e293b', maxWidth: 180, whiteSpace: 'normal' }} title={pac.nomeCompleto || oc.nomePaciente}>
                      <div>{pac.nomeCompleto || oc.nomePaciente || '--'}</div>
                      {bottleneck && (
                        <div
                          title={bottleneck.outras > 0 ? `+ ${bottleneck.outras} outra${bottleneck.outras > 1 ? 's' : ''} especialidade${bottleneck.outras > 1 ? 's' : ''} pendente${bottleneck.outras > 1 ? 's' : ''}` : undefined}
                          style={{ fontSize: 10, fontWeight: 600, color: bottleneck.lado === 'coordenacao' ? '#a16207' : '#1d4ed8', marginTop: 2, whiteSpace: 'nowrap' }}
                        >
                          ⏳ {bottleneck.lado === 'coordenacao' ? 'Coord.' : 'Adm.'} {bottleneck.area} · {elapsed(bottleneck.desde)}
                          {bottleneck.outras > 0 && ` +${bottleneck.outras}`}
                        </div>
                      )}
                    </td>
                    <td style={{ ...TD, color: '#6366f1', fontWeight: 600 }}>{pac.numIW || oc.numeroIW || '--'}</td>
                    <td style={{ ...TD }}>
                      {(() => {
                        const ehImplant = resolveOcTipo(oc) === 'implantacao';
                        const ehReimplant = ehImplant && pac.tipo === 'Reimplantação';
                        return (
                          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                            <span style={{ background: ehReimplant ? '#fce7f3' : ehImplant ? '#ede9fe' : '#fff7ed', color: ehReimplant ? '#be185d' : ehImplant ? '#7c3aed' : '#c2410c', padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
                              {['previa','reconciliado'].includes(oc.status) ? '🗒 Prévia' : ehReimplant ? 'Reimplant.' : ehImplant ? 'Implant.' : 'Hospitaliz.'}
                            </span>
                            {oc.previaHouveMudanca && <span style={{ background:'#fffbeb', color:'#92400e', border:'1px solid #fde68a', padding:'1px 6px', borderRadius:20, fontSize:9, fontWeight:700 }}>⚠️ PAD alterado</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ ...TD, color: '#64748b' }}>{pac.classificacao || '--'}</td>
                    <td style={{ ...TD, color: '#64748b', fontSize: 11, maxWidth: 130 }} title={pac.categoria || oc.categoria}>{pac.categoria || oc.categoria || '--'}</td>
                    <td style={{ ...TD, textAlign: 'center' }}>
                      <span style={{ color: (pac.possuiLiminar || oc.possuiLiminar) ? '#dc2626' : '#86efac', fontWeight: 700, fontSize: 11 }}>
                        {(pac.possuiLiminar || oc.possuiLiminar) ? 'SIM' : 'Não'}
                      </span>
                    </td>
                    <td style={{ ...TD, maxWidth: 110 }} title={pac.convenio || oc.convenio}>{pac.convenio || oc.convenio || '--'}</td>
                    <td style={{ ...TD }}>{fmtDate(oc.dataAbertura)}</td>
                    <td style={{ ...TD, fontWeight: 600, color: isOverdue ? '#dc2626' : '#475569' }}>
                      {isOverdue
                        ? <span style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: 6, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>⏰ VENCIDA</span>
                        : elapsed(oc.dataAbertura)
                      }
                    </td>
                    <td style={{ ...TD }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22c55e' : 'linear-gradient(90deg,#667eea,#764ba2)', borderRadius: 3, transition: 'width 0.4s' }} />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', width: 28, textAlign: 'right' }}>{pct}%</span>
                      </div>
                    </td>
                    <td style={{ ...TD }}><StatusBadge status={oc.status} /></td>
                    <td style={{ ...TD }}><PriorBadge p={autoPrio} /></td>
                  </tr>

                  {/* ─── EXPANDED TASKS ─────────────────────────────────────── */}
                  {isExp && (
                    <tr>
                      <td colSpan={14} style={{ padding: 0 }}>
                        <div style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                          {/* ── Inline detail strip ── */}
                          {editMode[oc.id] && (
                            /* ── EDIT FORM ── */
                            <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e8ecf4' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: 12 }}>
                                {[
                                  { label: 'Nome Completo', field: 'nomeCompleto', type: 'text' },
                                  { label: 'Convênio', field: 'convenio', type: 'text' },
                                  { label: 'Nº IW', field: 'numIW', type: 'text' },
                                  { label: 'Data Nasc.', field: 'dataNascimento', type: 'date' },
                                ].map(({ label, field, type }) => (
                                  <div key={field}>
                                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>{label.toUpperCase()}</div>
                                    <input
                                      type={type}
                                      value={editForm[oc.id]?.[field] || ''}
                                      disabled={field === 'numIW' && !isAdmin}
                                      onChange={e => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], [field]: e.target.value } }))}
                                      style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box', outline: 'none', fontFamily: 'Inter, sans-serif', background: (field === 'numIW' && !isAdmin) ? '#f1f5f9' : '#fff' }}
                                    />
                                  </div>
                                ))}
                                <div>
                                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>CLASSIFICAÇÃO</div>
                                  <select value={editForm[oc.id]?.classificacao || ''} onChange={e => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], classificacao: e.target.value } }))} style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, background: '#fff' }}>
                                    <option value="">—</option>
                                    {['ID', 'AD', 'Pontual'].map(o => <option key={o}>{o}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>CATEGORIA</div>
                                  <select value={editForm[oc.id]?.categoria || ''} onChange={e => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], categoria: e.target.value } }))} style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, background: '#fff' }}>
                                    <option value="">—</option>
                                    {['ID', 'AD', 'Pontual', 'Terapias'].map(o => <option key={o}>{o}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>TIPO (Novo/Reimplantação)</div>
                                  <select value={editForm[oc.id]?.tipo || ''} onChange={e => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], tipo: e.target.value } }))} style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, background: '#fff' }}>
                                    <option value="">—</option>
                                    {['Novo', 'Reimplantação'].map(o => <option key={o}>{o}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>TIPO DE OCORRÊNCIA</div>
                                  <select value={editForm[oc.id]?.tipoOcorrencia || ''} onChange={e => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], tipoOcorrencia: e.target.value } }))} style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, background: '#fff' }}>
                                    <option value="">—</option>
                                    {['Hospitalização', 'Implantação'].map(o => <option key={o}>{o}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>LIMINAR</div>
                                  <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                                    {[true, false].map(v => (
                                      <button key={String(v)} type="button" onClick={() => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], possuiLiminar: v } }))}
                                        style={{ padding: '5px 14px', borderRadius: 6, border: '1.5px solid', borderColor: editForm[oc.id]?.possuiLiminar === v ? (v ? '#dc2626' : '#16a34a') : '#e2e8f0', background: editForm[oc.id]?.possuiLiminar === v ? (v ? '#fef2f2' : '#f0fdf4') : '#fff', color: editForm[oc.id]?.possuiLiminar === v ? (v ? '#dc2626' : '#16a34a') : '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                        {v ? 'Sim' : 'Não'}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>OBSERVAÇÕES</div>
                                <textarea
                                  value={editForm[oc.id]?.observacoes || ''}
                                  onChange={e => setEditForm(prev => ({ ...prev, [oc.id]: { ...prev[oc.id], observacoes: e.target.value } }))}
                                  rows={2}
                                  style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                <button onClick={() => handleCancelEdit(oc.id)} style={{ padding: '7px 18px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                  Cancelar
                                </button>
                                <button onClick={() => handleSaveRequest(oc.id)} style={{ padding: '7px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                  🔐 Salvar
                                </button>
                              </div>
                            </div>
                          )}
                          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <colgroup>
                              <col style={{ width: 28 }} />   {/* indent */}
                              <col style={{ width: 190 }} />  {/* area */}
                              <col style={{ width: 95 }} />   {/* frequencia */}
                              <col style={{ width: 140 }} />  {/* responsavel */}
                              <col style={{ width: 120 }} />  {/* cooperativa */}
                              <col style={{ width: 90 }} />   {/* valor */}
                              <col style={{ width: 78 }} />   {/* abertura */}
                              <col style={{ width: 60 }} />   {/* tempo */}
                              <col style={{ width: 110 }} />  {/* status coordenação */}
                              <col style={{ width: 110 }} />  {/* status administrativo */}
                              <col style={{ width: 48 }} />   {/* chat */}
                            </colgroup>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ ...TH, background: '#f1f5f9' }}></th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Área / Setor</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Freq. / Detalhes</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Responsável</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Cooperativa</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Valor</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Abertura</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Tempo</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Status Coord.</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>Status Adm.</th>
                                <th style={{ ...TH, background: '#f1f5f9' }}>💬</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* PAD GROUP */}
                              {padTasks.length > 0 && (
                                <>
                                  <tr
                                    className="oc-row"
                                    onClick={() => setPadExpanded(p => ({ ...p, [oc.id]: !p[oc.id] }))}
                                    style={{ cursor: 'pointer', background: '#eef2ff', borderBottom: '1px solid #e2e8f0' }}
                                  >
                                    <td style={{ ...TD, textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>
                                      {padExpanded[oc.id] ? '▼' : '▶'}
                                    </td>
                                    <td style={{ ...TD, fontWeight: 700, color: '#4338ca' }} colSpan={9}>
                                      PAD — Plano de Atenção Domiciliar
                                      <span style={{ fontWeight: 400, color: '#6366f1', marginLeft: 8, fontSize: 11 }}>
                                        ({padTasks.filter(t => t.status === 'concluido').length}/{padTasks.length} concluídas)
                                      </span>
                                    </td>
                                    <td />
                                  </tr>
                                  {padExpanded[oc.id] && padTasks.map(t => (
                                    <TarefaRow key={t.id} t={t} oc={oc} users={users} cooperativas={cooperativas} empresasTerceiras={empresasTerceiras} indent onUpdate={handleUpdateTarefa} onChat={openChat} chatInfo={chatResumo[t.id]} onDelete={handleDeleteTarefa} isSuperAdmin={isSuperAdmin} TD={TD} SELECT_INLINE={SELECT_INLINE} />
                                  ))}
                                </>
                              )}



                              {/* ROOT TASKS */}
                              {rootTasks.map(t => (
                                <TarefaRow key={t.id} t={t} oc={oc} users={users} cooperativas={cooperativas} empresasTerceiras={empresasTerceiras} onUpdate={handleUpdateTarefa} onChat={openChat} chatInfo={chatResumo[t.id]} onDelete={handleDeleteTarefa} isSuperAdmin={isSuperAdmin} TD={TD} SELECT_INLINE={SELECT_INLINE} />
                              ))}

                              {tarefasVisiveis.length === 0 && (
                                <tr>
                                  <td colSpan={11} style={{ padding: '12px 20px', color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
                                    Nenhuma tarefa encontrada com os filtros atuais.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          {!editMode[oc.id] && (
                            /* ── VIEW MODE (detalhes do paciente) ── */
                            <div style={{ padding: '12px 20px', background: '#fff', borderTop: '1px solid #e8ecf4', display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: resolveOcTipo(oc) !== 'implantacao' ? '#fff7ed' : pac.tipo === 'Reimplantação' ? '#fce7f3' : '#ede9fe', color: resolveOcTipo(oc) !== 'implantacao' ? '#c2410c' : pac.tipo === 'Reimplantação' ? '#be185d' : '#7c3aed' }}>
                                {resolveOcTipo(oc) !== 'implantacao' ? '🟠 Hospitalização' : pac.tipo === 'Reimplantação' ? '🔵 Reimplantação' : '🔵 Implantação'}
                              </span>
                              {pac.convenio && (
                                <div><span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, display: 'block', letterSpacing: '0.04em' }}>CONVÊNIO</span><span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{pac.convenio}</span></div>
                              )}
                              {pac.classificacao && (
                                <div><span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, display: 'block', letterSpacing: '0.04em' }}>CLASSIFICAÇÃO</span><span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{pac.classificacao}</span></div>
                              )}
                              {pac.categoria && (
                                <div><span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, display: 'block', letterSpacing: '0.04em' }}>CATEGORIA</span><span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{pac.categoria}</span></div>
                              )}
                              {(pac.possuiLiminar || oc.possuiLiminar) && (
                                <div><span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, display: 'block', letterSpacing: '0.04em' }}>LIMINAR</span><span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>⚠️ Sim</span></div>
                              )}

                              {pac.tipo && (
                                <div><span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, display: 'block', letterSpacing: '0.04em' }}>TIPO</span><span style={{ fontSize: 12, color: '#334155' }}>{pac.tipo}</span></div>
                              )}
                              {oc.criadoPorNome && (
                                <div><span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, display: 'block', letterSpacing: '0.04em' }}>CRIADO POR</span><span style={{ fontSize: 12, color: '#334155' }}>{oc.criadoPorNome}</span></div>
                              )}
                              {(pac.observacoes || oc.observacoes) && (
                                <div style={{ flexBasis: '100%', padding: '6px 10px', background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>📝 {pac.observacoes || oc.observacoes}</div>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                                <button onClick={() => handleStartEdit(oc)} style={{ padding: '5px 14px', borderRadius: 7, border: '1.5px solid #6366f1', background: '#fff', color: '#6366f1', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                  ✏️ Editar
                                </button>
                                {isSuperAdmin && (
                                  <button onClick={() => handleDeleteOcorrencia(oc)} style={{ padding: '5px 14px', borderRadius: 7, border: '1.5px solid #dc2626', background: '#fff', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                    🗑️ Excluir
                                  </button>
                                )}
                                <span style={{ fontSize: 10, color: '#9ca3af', letterSpacing: '0.04em' }}>PROGRESSO</span>
                                <div style={{ width: 80, height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22c55e' : 'linear-gradient(90deg,#667eea,#764ba2)', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>{pct}%</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ComentariosChat
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        ocorrenciaId={chatConfig.ocorrenciaId}
        tarefaId={chatConfig.tarefaId}
        tarefaTitulo={chatConfig.tarefaTitulo}
      />

      {passwordModal && (
        <PasswordModal
          saving={savingEdit[passwordModal.ocId]}
          onConfirm={handleConfirmSave}
          onCancel={() => setPasswordModal(null)}
        />
      )}

      {showWizard && (
        <WizardOcorrencia onClose={() => setShowWizard(false)} />
      )}
      </>
    </div>
  );
}

// ─── Password Modal ───────────────────────────────────────────────────────────
function PasswordModal({ onConfirm, onCancel, saving }) {
  const [pwd, setPwd] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '32px 36px', width: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: 12 }}>🔐</div>
        <h3 style={{ margin: '0 0 8px', textAlign: 'center', color: '#1e293b', fontSize: '1rem' }}>Confirmar edição</h3>
        <p style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', margin: '0 0 20px', lineHeight: 1.5 }}>
          Digite sua senha de acesso para salvar as alterações.
        </p>
        <input
          type="password"
          value={pwd}
          onChange={e => setPwd(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && pwd && onConfirm(pwd)}
          placeholder="Sua senha"
          autoFocus
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: '0.95rem', marginBottom: 16, boxSizing: 'border-box', outline: 'none', fontFamily: 'Inter, sans-serif' }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            Cancelar
          </button>
          <button
            onClick={() => pwd && onConfirm(pwd)}
            disabled={!pwd || saving}
            style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: pwd && !saving ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#e2e8f0', color: pwd && !saving ? '#fff' : '#94a3b8', fontWeight: 700, cursor: pwd && !saving ? 'pointer' : 'not-allowed', fontSize: 13 }}
          >
            {saving ? '⏳ Salvando...' : '✅ Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-especialidade Row ───────────────────────────────────────────────────
function SubEspRow({ sub, freq, meta, t, cooperativas, onSubUpdate, TD, SELECT_INLINE }) {
  const [hover, setHover] = useState(false);
  const dateStr = t.dataAbertura
    ? new Date(t.dataAbertura.toDate ? t.dataAbertura.toDate() : t.dataAbertura).toLocaleDateString('pt-BR')
    : '--';
  return (
    <tr
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ background: hover ? '#f5f3ff' : '#faf5ff', borderBottom: '1px dashed #ede9fe' }}
    >
      <td style={{ ...TD, paddingLeft: 36, color: '#c4b5fd', fontSize: 11 }}>└</td>
      <td style={{ ...TD, fontSize: 11, color: '#6d28d9', fontWeight: 600, whiteSpace: 'normal' }}>{sub}</td>
      <td style={{ ...TD, fontSize: 11, color: '#0369a1', fontWeight: 600 }}>{freq || '—'}</td>
      <td style={{ ...TD, color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>—</td>
      <td style={{ ...TD, padding: '2px 8px' }}>
        <select
          value={meta?.cooperativa || ''}
          onChange={e => onSubUpdate(sub, 'cooperativa', e.target.value)}
          style={SELECT_INLINE}
        >
          <option value="">— Cooperativa —</option>
          {(cooperativas || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td style={{ ...TD, color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>—</td>
      <td style={{ ...TD, fontSize: 11, color: '#94a3b8' }}>{dateStr}</td>
      <td style={{ ...TD, fontSize: 11, color: '#94a3b8' }}>{elapsed(t.dataAbertura)}</td>
      <td style={{ ...TD, color: '#94a3b8', fontSize: 11 }}>—</td>
      <td style={{ ...TD, color: '#94a3b8', fontSize: 11 }}>—</td>
      <td />
    </tr>
  );
}

// Áreas que exibem itens selecionados como badges coloridos
const BADGE_AREAS = ['Prontuário','Mobiliário','Gases Medicinais','Equipamentos','Farmácia','Logística','Suprimentos'];
const AREA_BADGE_COLOR = {
  'Prontuário':       '#2c7a7b',
  'Mobiliário':       '#805ad5',
  'Gases Medicinais': '#3182ce',
  'Equipamentos':     '#b7791f',
  'Farmácia':         '#c05621',
  'Logística':        '#4a5568',
  'Suprimentos':      '#276749',
};

// Itens disponíveis por área para o editor admin
const AREA_EDIT_ITEMS = {
  'Equipamentos':     ['Bomba de Infusão','Monitor Multiparamétrico','Aspirador','Concentrador de O₂','CPAP/BIPAP','Nebulizador','Balança'],
  'Mobiliário':       ['Cama Hospitalar','Colchão Piramidal','Mesa de Refeição','Poltrona Reclinável','Grade Lateral','Suporte de Soro','Cadeira de Rodas'],
  'Gases Medicinais': ['Cilindro de O₂','Regulador de Pressão','Máscara de O₂','Cânula Nasal','Umidificador','Oxímetro de Pulso'],
  'Prontuário':       ['Pasta Fixa','Prontuário Rotativo','Prescrição de Enfermagem','Prescrição Médica','Lista de Verificação','Folha de Evolução','Registro de Sinais Vitais'],
  'Farmácia':         ['Medicamentos','Material de Curativo','Fraldas','Dieta Enteral','Sondas/Cateteres','Luvas e EPIs'],
  'Logística':        ['Equipe Alocada','Transporte Confirmado','Kit de Alta Preparado','Família Orientada'],
  'Exames':           ['Hemograma','Bioquímica','Urocultura','Hemocultura','Raio-X Tórax','ECG','Gasometria','Glicemia'],
  'Remoções':         ['Alta Médica Assinada','Laudo de Remoção','Ambulância Solicitada','Acompanhante Confirmado','Relatório de Transferência'],
};

// ─── Task Row Sub-component ───────────────────────────────────────────────────
function TarefaRow({ t, oc, users, cooperativas, empresasTerceiras, indent, onUpdate, onChat, chatInfo, onDelete, isSuperAdmin, TD, SELECT_INLINE }) {
  const [hover, setHover] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [subExpanded, setSubExpanded] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [editDescValue, setEditDescValue] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const statusColor = { pendente: '#f1f5f9', em_andamento: '#dbeafe', concluido: '#dcfce7' }[t.status] || '#f1f5f9';
  const statusAdmColor = { pendente: '#f1f5f9', em_andamento: '#dbeafe', concluido: '#dcfce7' }[t.statusAdm] || '#f1f5f9';

  const openEdit = () => { setEditDescValue(t.descricao || ''); setEditingDesc(true); };
  const cancelEdit = () => setEditingDesc(false);
  const saveEdit = async () => {
    setSavingDesc(true);
    await onUpdate(oc.id, t.id, 'descricao', editDescValue.trim());
    setSavingDesc(false);
    setEditingDesc(false);
  };

  const isBadgeArea = BADGE_AREAS.includes(t.area);
  const availableItems = AREA_EDIT_ITEMS[t.area] || [];
  const editSelected = new Set((editDescValue || '').split('|').map(s => s.trim()).filter(Boolean));
  const toggleEditItem = (item) => {
    const next = new Set(editSelected);
    if (next.has(item)) next.delete(item); else next.add(item);
    setEditDescValue([...next].join(' | '));
  };

  // Sub-especialidades ativas (valor preenchido e != 'N/A') — só para tarefas PAD com indent
  const activeSubEntries = indent && t.subspecialidades
    ? Object.entries(t.subspecialidades).filter(([, f]) => f && f !== 'N/A')
    : null;
  const hasSubRows = activeSubEntries && activeSubEntries.length > 0;

  // Atualiza subspecialidades_meta sem dot notation (suporta chaves com espaços/pontos)
  const handleSubUpdate = (sub, field, value) => {
    const currentMeta = t.subspecialidades_meta || {};
    const newMeta = { ...currentMeta, [sub]: { ...(currentMeta[sub] || {}), [field]: value } };
    onUpdate(oc.id, t.id, 'subspecialidades_meta', newMeta);
  };

  return (
    <React.Fragment>
      <tr
        className="tarefa-row"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ background: hover ? '#f0f9ff' : '#fff', borderBottom: '1px dashed #e2e8f0' }}
      >
        <td style={{ ...TD, paddingLeft: indent ? 28 : 12, color: '#94a3b8', fontSize: 11 }}>
          {indent ? '└' : ''}
        </td>
        <td style={{ ...TD, fontWeight: 600, color: '#334155', whiteSpace: 'normal', lineHeight: 1.3 }}>
          {BADGE_AREAS.includes(t.area) ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: AREA_BADGE_COLOR[t.area] || '#2c7a7b', marginRight: 2, flexShrink: 0 }}>{t.area}</span>
              {t.descricao ? t.descricao.split(/\|/).map((item, idx) => {
                const label = item.trim();
                if (!label) return null;
                const isDone = t.status === 'concluido';
                return (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      background: isDone ? '#dcfce7' : '#dbeafe',
                      color: isDone ? '#16a34a' : '#1e40af',
                      border: `1px solid ${isDone ? '#bbf7d0' : '#bfdbfe'}`,
                      padding: '2px 9px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      textDecoration: isDone ? 'line-through' : 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isDone ? '✓ ' : ''}{label}
                  </span>
                );
              }) : (
                <span style={{ fontWeight: 400, color: '#a0aec0', fontSize: 11, fontStyle: 'italic' }}>
                  nenhum item selecionado
                </span>
              )}
            </div>
          ) : hasSubRows ? (
            <div
              onClick={() => setSubExpanded(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ fontSize: 9, color: '#7c3aed', flexShrink: 0 }}>{subExpanded ? '▼' : '▶'}</span>
              <span style={{ color: '#4338ca', fontWeight: 700 }}>{t.area.replace('PAD - ', '')}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {!indent && t.descricao && (
                <span
                  onClick={e => { e.stopPropagation(); setDescOpen(o => !o); }}
                  title="Ver detalhes"
                  style={{ cursor: 'pointer', fontSize: 8, color: '#94a3b8', userSelect: 'none', display: 'inline-block', transition: 'transform 0.15s', transform: descOpen ? 'rotate(90deg)' : 'none', flexShrink: 0 }}
                >▶</span>
              )}
              {/* sub-especialidade como sub-texto para WizardOcorrencia */}
              <div>
                <span title={t.area}>{indent ? t.area.replace('PAD - ', '') : t.area}</span>
                {indent && t.subEspecialidade && (
                  <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginTop: 1 }}>{t.subEspecialidade}</div>
                )}
              </div>
            </div>
          )}
          {/* Tarefas não-badge: descricao colapsável */}
          {!BADGE_AREAS.includes(t.area) && !indent && descOpen && t.descricao && (
            <div style={{ fontSize: 10, color: '#475569', fontWeight: 400, marginTop: 4, lineHeight: 1.5, background: '#f1f5f9', borderRadius: 5, padding: '4px 7px' }}>{t.descricao}</div>
          )}
        </td>
        {/* Freq. / Detalhes: PAD mostra frequência; demais áreas mostram descrição resumida */}
        <td style={{
          ...TD,
          fontSize: 11,
          color: (BADGE_AREAS.includes(t.area) || BADGE_AREAS.includes(t.areaPai))
            ? '#cbd5e1'
            : (indent && !hasSubRows)
              ? '#0369a1'
              : t.descricao ? '#475569' : '#94a3b8',
          fontWeight: (BADGE_AREAS.includes(t.area) || BADGE_AREAS.includes(t.areaPai))
            ? 400
            : (indent && !hasSubRows)
              ? 600 : 400,
          whiteSpace: 'normal',
          lineHeight: 1.3
        }}>
          {t.areaPai === 'Prontuário' || t.area === 'Prontuário'
            ? '—'
            : indent && !hasSubRows
              ? (t.frequencia || t.descricao || '—')
              : (!indent && t.descricao
                  ? <span title={t.descricao} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.descricao}</span>
                  : '—'
                )
          }
        </td>
        <td style={{ ...TD, padding: '2px 10px' }}>
          <select
            value={t.responsavelUid || ''}
            onChange={e => {
              const uid = e.target.value;
              const usuario = users.find(u => u.id === uid);
              onUpdate(oc.id, t.id, {
                responsavelUid: uid,
                responsavelNome: usuario ? usuario.name : ''
              });
            }}
            style={SELECT_INLINE}
          >
            <option value="">— Atribuir —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </td>
        {/* Cooperativa / Empresa Terceira */}
        <td style={{ ...TD, padding: '2px 10px' }}>
          {t.areaPai === 'PAD' ? (
            <select
              value={t.cooperativa || ''}
              onChange={e => onUpdate(oc.id, t.id, 'cooperativa', e.target.value)}
              style={SELECT_INLINE}
            >
              <option value="">— Cooperativa —</option>
              {(cooperativas || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (t.area === 'Mobiliário' || t.area === 'Equipamentos' || t.areaPai === 'Mobiliário' || t.areaPai === 'Equipamentos') ? (
            <select
              value={t.cooperativa || ''}
              onChange={e => onUpdate(oc.id, t.id, 'cooperativa', e.target.value)}
              style={SELECT_INLINE}
            >
              <option value="">— Empresa Terceira —</option>
              {(empresasTerceiras || []).map(emp => <option key={emp} value={emp}>{emp}</option>)}
            </select>
          ) : (
            <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>
          )}
        </td>
        {/* Valor (só PAD) */}
        <td style={{ ...TD, padding: '2px 10px' }}>
          {t.areaPai !== 'PAD' ? (
            <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>
          ) : (
            <input
              type="text"
              defaultValue={t.valor || ''}
              placeholder="R$"
              onBlur={e => {
                const v = e.target.value.trim();
                if (v !== (t.valor || '')) onUpdate(oc.id, t.id, 'valor', v);
              }}
              style={{ ...SELECT_INLINE, cursor: 'text' }}
            />
          )}
        </td>
        <td style={{ ...TD, fontSize: 11 }}>{t.dataAbertura ? new Date(t.dataAbertura.toDate ? t.dataAbertura.toDate() : t.dataAbertura).toLocaleDateString('pt-BR') : '--'}</td>
        <td style={{ ...TD, fontWeight: 600, fontSize: 11, color: '#475569' }}>{elapsed(t.dataAbertura)}</td>
        {/* Status Coordenação */}
        <td style={{ ...TD, padding: '2px 10px' }}>
          {t.area === 'Remoções' && oc.captacaoId && !isSuperAdmin ? (
            <span title="Status gerenciado em Gestão de Remoções" style={{ ...SELECT_INLINE, background: statusColor, fontWeight: 700, cursor:'default', border:'none', padding:'4px 10px', borderRadius:6, fontSize:'0.78rem', display:'inline-block' }}>
              {t.status === 'pendente' ? 'Pendente' : t.status === 'em_andamento' ? 'Em Andamento' : 'Concluído'}
            </span>
          ) : (
            <select
              value={t.status || 'pendente'}
              onChange={e => onUpdate(oc.id, t.id, 'status', e.target.value)}
              title={t.area === 'Remoções' && oc.captacaoId ? 'Normalmente sincronizado pela Gestão de Remoções — alteração manual de admin master' : undefined}
              style={{ ...SELECT_INLINE, background: statusColor, fontWeight: 700 }}
            >
              <option value="pendente">Pendente</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="concluido">Concluído</option>
            </select>
          )}
        </td>
        {/* Status Administrativo (só PAD) */}
        <td style={{ ...TD, padding: '2px 10px' }}>
          {t.areaPai !== 'PAD' ? (
            <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>
          ) : (
            <select
              value={t.statusAdm || 'pendente'}
              onChange={e => onUpdate(oc.id, t.id, 'statusAdm', e.target.value)}
              style={{ ...SELECT_INLINE, background: statusAdmColor, fontWeight: 700 }}
            >
              <option value="pendente">Pendente</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="concluido">Concluído</option>
            </select>
          )}
        </td>
        <td style={{ ...TD, textAlign: 'center', padding: '2px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            {(() => {
              const total = chatInfo?.total || 0;
              const naoLida = total > 0 && chatInfo.ultimoAutor !== auth.currentUser?.uid && chatInfo.ultimo > getChatLido(t.id);
              return (
                <button onClick={() => onChat(oc.id, t.id, t.area)} title={naoLida ? 'Comentários — mensagem nova' : total ? `Comentários (${total})` : 'Comentários'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, opacity: total ? 1 : 0.7, position: 'relative', padding: '0 4px' }}>
                  💬
                  {total > 0 && (
                    <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7, fontSize: 9, fontWeight: 800, lineHeight: '14px', textAlign: 'center', color: '#fff', background: naoLida ? '#ef4444' : '#94a3b8' }}>
                      {total}
                    </span>
                  )}
                </button>
              );
            })()}
            {isSuperAdmin && (
              <>
                <button onClick={openEdit} title="Editar conteúdo" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.7, color: '#d97706' }}>✏️</button>
                <button onClick={() => onDelete(oc.id, t.id, t.area)} title="Excluir etapa" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.7, color: '#dc2626' }}>🗑️</button>
              </>
            )}
          </div>
        </td>
      </tr>
      {/* ── Painel de edição admin ── */}
      {editingDesc && (
        <tr>
          <td colSpan={11} style={{ padding: '12px 18px', background: '#fffbeb', borderBottom: '2px solid #fbbf24' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 10 }}>
              ✏️ Editando: <span style={{ color: '#b45309' }}>{t.area}</span>
            </div>

            {/* Checklist para áreas com itens predefinidos */}
            {isBadgeArea && availableItems.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#78350f', marginBottom: 6, fontWeight: 600 }}>Itens predefinidos:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availableItems.map(item => {
                    const checked = editSelected.has(item);
                    return (
                      <label key={item} onClick={() => toggleEditItem(item)} style={{
                        display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                        background: checked ? '#fef3c7' : '#fff',
                        border: `1.5px solid ${checked ? '#f59e0b' : '#e2e8f0'}`,
                        borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: checked ? 700 : 400,
                        color: checked ? '#92400e' : '#64748b', userSelect: 'none', transition: 'all 0.1s',
                      }}>
                        <span style={{ fontSize: 10 }}>{checked ? '☑' : '☐'}</span>
                        {item}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Campo de texto livre (sempre visível) */}
            <div style={{ fontSize: 11, color: '#78350f', marginBottom: 4, fontWeight: 600 }}>
              {isBadgeArea ? 'Ou edite manualmente (itens separados por |):' : 'Conteúdo / descrição:'}
            </div>
            <textarea
              value={editDescValue}
              onChange={e => setEditDescValue(e.target.value)}
              rows={isBadgeArea ? 2 : 4}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #fbbf24', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', background: '#fff', outline: 'none' }}
              placeholder={isBadgeArea ? 'Ex: Bomba de Infusão | Nebulizador' : 'Descrição da tarefa...'}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={saveEdit} disabled={savingDesc} style={{ padding: '6px 18px', borderRadius: 7, border: 'none', background: savingDesc ? '#e2e8f0' : '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 12, cursor: savingDesc ? 'not-allowed' : 'pointer' }}>
                {savingDesc ? '⏳ Salvando...' : '✅ Salvar'}
              </button>
              <button onClick={cancelEdit} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          </td>
        </tr>
      )}
      {/* Sub-especialidade rows (quando expandido) */}
      {hasSubRows && subExpanded && activeSubEntries.map(([sub, freq]) => (
        <SubEspRow
          key={sub}
          sub={sub}
          freq={freq}
          meta={t.subspecialidades_meta?.[sub]}
          t={t}
          cooperativas={cooperativas}
          onSubUpdate={handleSubUpdate}
          TD={TD}
          SELECT_INLINE={SELECT_INLINE}
        />
      ))}
    </React.Fragment>
  );
}
